import { XIcon } from "lucide-react"
import type { ReactNode } from "react"

import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * InlineNotification — non-floating, in-flow status banner.
 *
 * Use this instead of Sonner toasts for confirmations that should stay
 * visible until the user has acknowledged them or navigates away
 * (account recovery, submission confirmations, blocking warnings).
 *
 * Design rules honored:
 * - **Sharp geometry** (NaturStore-safe): accent via `border-l-4`,
 *   no `rounded-*` utilities. Renders rectilinear in every theme.
 * - **Restrained register**: muted background tint + matching foreground,
 *   no full-saturation fills.
 * - **A11y**: `role="status"` for success/info (polite live region),
 *   `role="alert"` for warning/error (assertive). Resolved automatically
 *   from `variant`; override with the `role` prop if needed.
 *
 * Pairs with any lucide icon passed via `icon` (rendered at size-4,
 * top-aligned with the first text line).
 */
const inlineNotificationVariants = cva(
  "flex items-start gap-3 border-l-4 px-4 py-3 text-sm [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        success: "border-success bg-success/10 text-success",
        info: "border-primary bg-primary/10 text-primary",
        warning: "border-warning bg-warning/10 text-warning",
        error: "border-destructive bg-destructive/10 text-destructive",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  },
)

export interface InlineNotificationProps extends VariantProps<typeof inlineNotificationVariants> {
  children: ReactNode
  /** Optional leading icon (typically a lucide-react icon at default size). */
  icon?: ReactNode
  /** When provided, renders a dismiss button that calls this on click. */
  onDismiss?: () => void
  /** Override the automatically-resolved ARIA role. */
  role?: "status" | "alert"
  /** Accessible label for the dismiss button. Defaults to "Dismiss". */
  dismissLabel?: string
  className?: string
}

export function InlineNotification({
  variant = "info",
  children,
  icon,
  onDismiss,
  role,
  dismissLabel = "Dismiss",
  className,
}: InlineNotificationProps) {
  const resolvedRole = role ?? (variant === "warning" || variant === "error" ? "alert" : "status")

  return (
    <div
      data-slot="inline-notification"
      data-variant={variant}
      role={resolvedRole}
      className={cn(inlineNotificationVariants({ variant }), className)}
    >
      {icon ? (
        <span aria-hidden="true" className="mt-0.5 shrink-0">
          {icon}
        </span>
      ) : null}
      <div className="flex-1 text-pretty">{children}</div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="shrink-0 cursor-pointer p-1 opacity-60 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current/40"
        >
          <XIcon className="size-4" />
        </button>
      ) : null}
    </div>
  )
}
